import * as THREE from 'three'
import { types, getParent, Instance, ISimpleType } from 'mobx-state-tree'
import { View } from 'src/api/misc/View'
import { Padding } from 'src/api/misc/View/Padding'
import { getDiameter } from 'src/utils/getDiameter'
import sides, { orbit, Side } from 'src/constants/sides'
import {
  orthographicCameraType,
  perspectiveCameraType,
  CameraType
} from 'src/constants/cameras'
import {
  ISideviewBase,
  IFit,
  ISize,
  ISideview,
  IOrbitview,
  IView
} from './types'
import { Vector3 } from 'src/api/misc/Vector3'

const getCenter = (boundings: THREE.Box3): [number, number, number] => {
  const center = new THREE.Vector3()
  boundings.getCenter(center)
  return center.clone().toArray() as [number, number, number]
}

const SideviewBase: ISideviewBase = types
  .model({
    cameraType: types.optional(
      types.enumeration([perspectiveCameraType, orthographicCameraType]),
      orthographicCameraType
    ),
    controlsEnabled: types.optional(types.boolean, false),
    panEnabled: types.optional(types.boolean, true),
    zoomEnabled: types.optional(types.boolean, true),
    matrix: types.maybe(
      types.refinement(
        types.array(types.number),
        (value) => value?.length === 16
      )
    ),
    zoom: types.optional(types.number, 1),
    zoomFit: types.maybeNull(types.number),
    zoomLevel: types.optional(types.number, 0),
    padding: types.optional(Padding, { top: 0, right: 0, bottom: 0, left: 0 }),
    // layers: types.model()
    position: types.optional(types.array(types.number), [0, 0, 0])
  })
  .views((self) => {
    const padding = getParent<Instance<IView>>(self, 2).padding
    return {
      get view(): {
        offsetX: number
        offsetY: number
      } {
        return {
          offsetX: (self.padding.right + self.padding.left) / 2,
          offsetY: (self.padding.top + self.padding.bottom) / 2
        }
      }
    }
  })
  .volatile((self) => ({
    size: undefined as { width: number; height: number } | undefined,
    target: getCenter((getParent(self, 2) as any).draft.boundings),
    distance: 500
  }))
  .actions((self) => {
    return {
      setPosition(position: [number, number, number]) {
        self.position.replace(position)
      },
      setTarget(position: [number, number, number]) {
        self.target = position
      },
      resetTarget() {
        self.target = getCenter((getParent(self, 2) as any).draft.boundings)
      },
      zoomIn() {
        const next = self.zoom * 2
        // console.log(self.zoom, next)
        self.zoom = next
      },
      zoomOut() {
        self.zoom = self.zoom / 2
      },
      enable() {
        self.controlsEnabled = true
      },
      enableZoom() {
        self.zoomEnabled = true
      },
      enablePan() {
        self.panEnabled = true
      },
      disable() {
        self.controlsEnabled = false
      },
      disableZoom() {
        self.zoomEnabled = false
      },
      disablePan() {
        self.panEnabled = false
      }
    }
  })
  .actions((self) => ({
    fit: (({ camera, object: obj, size: givenSize, padding }) => {
      //
      const object =
        obj ||
        ((getParent(self, 2) as Instance<typeof View>).draft
          .boundings as THREE.Box3)
      if (!givenSize && self.size === undefined) return
      let size: ISize
      if (!givenSize) {
        size = { ...self.size } as ISize
      } else {
        size = { ...givenSize }
        self.size = size
      }
      if (padding === undefined) {
        size.width = size.width - self.view.offsetX * 2
        size.height = size.height - self.view.offsetY * 2
      } else {
        const { right = 0, left = 0, top = 0, bottom = 0 } = padding
        size.width = size.width - right - left
        size.height = size.height - top - bottom
      }

      const diameter = getDiameter(object)
      const aspect = size.width / size.height
      let zoom
      if (self.cameraType === orthographicCameraType) {
        console.log('fit orthographic')
        zoom = aspect > 1 ? size.height / diameter : size.width / diameter
      } else {
        console.log('fit perspective', self.zoom)
        // https://stackoverflow.com/questions/44849861/how-to-adjust-a-three-js-perspective-cameras-field-of-view-to-fit-a-sphere

        const distance = self.distance
        const radius = diameter / 2
        const fov = 50
        const temporaryFov = 2 * Math.asin(radius / distance) //* THREE.MathUtils.RAD2DEG

        const nextFov = 2 * Math.atan(Math.tan(temporaryFov / 2) / aspect)

        zoom = (Math.tan(THREE.MathUtils.DEG2RAD * 0.5 * fov) / nextFov) * 2
      }
      if (zoom === Infinity) zoom = 1

      if (!!padding && padding !== self.padding) {
        self.padding = { ...self.padding, ...padding }
      }

      self.zoom = zoom
      self.resetTarget()
    }) as IFit
  }))

export const Sideview: ISideview = SideviewBase.named('Sideview').props({
  side: types.refinement(types.identifier as ISimpleType<Side>, (identifier) =>
    sides.includes(identifier)
  )
})

export const Orbitview: IOrbitview = SideviewBase.named('OrbitView')
  .props({
    side: types.optional(types.identifier as ISimpleType<typeof orbit>, orbit),
    cameraType: types.optional(
      types.enumeration([perspectiveCameraType, orthographicCameraType]),
      perspectiveCameraType
    )
  })
  .actions((self) => ({
    setCameraType(cameraType: CameraType) {
      self.cameraType = cameraType
    },
    switchCameraType() {
      self.cameraType =
        self.cameraType === orthographicCameraType
          ? perspectiveCameraType
          : orthographicCameraType
    }
  }))
  .volatile((self) => {
    const orthographicCamera = new THREE.OrthographicCamera(0, 0, 0, 0)
    const perspectiveCamera = new THREE.PerspectiveCamera()

    return { orthographicCamera: orthographicCamera }
  })
